// Article HTML processor — runs once per post in a useMemo.
//
// Goal: take whatever HTML the post happens to ship with and *infer* a rich
// reading experience without any extra author input. This handles the
// real-world mess in the Instabridge corpus where most posts use
// <p><strong>Section title</strong></p> instead of proper <h2>.
//
// Returns: { html, toc, faqs, quickAnswer, primaryImage }
//   html         — transformed HTML string ready for dangerouslySetInnerHTML
//   toc          — [{ id, text, level }] in document order, FAQ section excluded
//   faqs         — [{ q, a }] extracted FAQ items (may be empty)
//   quickAnswer  — short (≈40-80 word) plain-text summary or null
//
// All transforms are idempotent and pure-DOM (DOMParser), no React/JS in HTML.

const CALLOUT_PATTERNS = [
  // [label-regex, type, color-class-name]
  [/^(note|fyi)\b/i,                            "note"],
  [/^(tip|pro tip|hot tip|quick tip)\b/i,       "tip"],
  [/^(important|warning|caution|heads up)\b/i,  "warning"],
  [/^(key takeaway|bottom line|in short|tl;dr)\b/i, "takeaway"],
  [/^(example|for example|case study)\b/i,      "example"],
];

const FAQ_HEADING_RE = /^(frequently asked questions|faq|faqs|common questions|questions and answers)$/i;

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&[a-z]+;/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

const stripTags = (html) => html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();

const textOf = (el) => (el?.textContent || "").trim();

/**
 * Promote a `<p><strong>Heading</strong></p>` (no other content) into an
 * actual `<h2>` element. Many imported posts use this pattern instead of
 * semantic headings. The detection is intentionally strict — we only
 * promote when the paragraph contains EXACTLY one <strong> with nothing
 * else (whitespace doesn't count), and the strong text reads like a
 * heading (3–14 words, no terminal period unless it's a question mark).
 */
function promotePseudoHeadings(doc) {
  const ps = Array.from(doc.querySelectorAll("p"));
  for (const p of ps) {
    // Reject if other text content beyond the <strong> exists.
    const childNodes = Array.from(p.childNodes).filter(
      (n) => !(n.nodeType === 3 && !n.textContent.trim())
    );
    if (childNodes.length !== 1) continue;
    const onlyChild = childNodes[0];
    if (onlyChild.nodeType !== 1 || onlyChild.tagName.toLowerCase() !== "strong") continue;
    const text = textOf(onlyChild);
    if (!text) continue;
    const words = text.split(/\s+/);
    // Heuristics: heading-like length, no trailing period (other than '?')
    if (words.length < 2 || words.length > 18) continue;
    if (/[.!]$/.test(text)) continue;
    // Promote.
    const h2 = doc.createElement("h2");
    h2.textContent = text;
    p.replaceWith(h2);
  }
}

/**
 * Remove a duplicated H2 at the very top of the body that just repeats the
 * page <h1>. The CMS-imported posts often re-include the title as the first
 * paragraph or H2.
 */
function dedupeLeadingTitle(doc, title) {
  if (!title) return;
  const norm = (s) => s.toLowerCase().replace(/\s+/g, " ").trim();
  const t = norm(title);
  const first = doc.body.firstElementChild;
  if (!first) return;
  if (first.tagName === "H1" || first.tagName === "H2") {
    if (norm(textOf(first)) === t) first.remove();
  }
}

/**
 * Add stable scroll-anchor IDs to every H2 + H3 and a scroll-margin so they
 * don't get tucked under the sticky nav.
 */
function addHeadingIds(doc) {
  const seen = new Map();
  for (const h of doc.querySelectorAll("h2, h3")) {
    let id = slugify(textOf(h)) || "section";
    const n = seen.get(id) || 0;
    seen.set(id, n + 1);
    if (n > 0) id = `${id}-${n + 1}`;
    h.setAttribute("id", id);
  }
}

/**
 * Detect FAQ section. Two signals (either triggers extraction):
 *   1. An H2 whose text matches "Frequently Asked Questions" etc., followed
 *      by Q/A pairs (H3 question + paragraph answer).
 *   2. A trailing cluster (3+) of consecutive H2s ending in "?" with their
 *      following paragraphs as answers.
 * In both cases we REMOVE those nodes from the body HTML and return the
 * structured Q/A array for the FAQAccordion component.
 */
function extractFaqs(doc) {
  const faqs = [];

  // Pattern 1: H2 "FAQ" heading + H3 questions.
  for (const h2 of Array.from(doc.querySelectorAll("h2"))) {
    if (!FAQ_HEADING_RE.test(textOf(h2))) continue;
    // Walk forward collecting H3 questions and their following paragraphs.
    let node = h2.nextElementSibling;
    const toRemove = [h2];
    while (node && !(node.tagName === "H2")) {
      const next = node.nextElementSibling;
      if (node.tagName === "H3") {
        const q = textOf(node);
        // Collect <p>+<ul>+<ol> until next H3 or H2 as the answer.
        const ansNodes = [];
        let after = node.nextElementSibling;
        while (after && !["H2", "H3"].includes(after.tagName)) {
          ansNodes.push(after);
          after = after.nextElementSibling;
        }
        const aHtml = ansNodes.map((n) => n.outerHTML).join("");
        if (q && aHtml) faqs.push({ q, a: aHtml });
        toRemove.push(node, ...ansNodes);
      } else if (!toRemove.includes(node)) {
        toRemove.push(node);
      }
      node = next;
    }
    if (faqs.length) toRemove.forEach((n) => n.remove());
    if (faqs.length) return faqs;
  }

  // Pattern 2: trailing cluster of "?" H2s. (Less common but seen.)
  const allH2 = Array.from(doc.querySelectorAll("h2"));
  const tail = [];
  for (let i = allH2.length - 1; i >= 0; i--) {
    if (textOf(allH2[i]).trim().endsWith("?")) tail.unshift(allH2[i]);
    else break;
  }
  if (tail.length >= 3) {
    for (const h2 of tail) {
      const q = textOf(h2);
      const ansNodes = [];
      let after = h2.nextElementSibling;
      while (after && !["H2", "H3"].includes(after.tagName)) {
        ansNodes.push(after);
        after = after.nextElementSibling;
      }
      const aHtml = ansNodes.map((n) => n.outerHTML).join("");
      if (q && aHtml) faqs.push({ q, a: aHtml });
      h2.remove();
      ansNodes.forEach((n) => n.remove());
    }
  }
  return faqs;
}

/**
 * Transform paragraphs starting with "<strong>Note:</strong> ..." (and
 * similar labels) into a styled callout box. Authors didn't write these as
 * callouts, but the *intent* is the same and the reader benefit is real.
 */
function applyCallouts(doc) {
  for (const p of Array.from(doc.querySelectorAll("p"))) {
    const strong = p.firstElementChild;
    if (!strong || strong.tagName !== "STRONG") continue;
    const label = textOf(strong).replace(/[:：]\s*$/, "");
    const match = CALLOUT_PATTERNS.find(([re]) => re.test(label));
    if (!match) continue;
    const [, type] = match;
    // Move trailing text out of the <strong> handling: keep label as title,
    // rest of paragraph as body.
    strong.remove();
    const bodyText = p.innerHTML.replace(/^[:：\s]+/, "").trim();
    if (!bodyText) continue;
    const aside = doc.createElement("aside");
    aside.className = `blog-callout blog-callout--${type}`;
    aside.setAttribute("data-callout-type", type);
    aside.innerHTML = `<div class="blog-callout__label">${label}</div><div class="blog-callout__body">${bodyText}</div>`;
    p.replaceWith(aside);
  }
}

/**
 * Style blockquotes as pull-quotes (visual treatment only — we don't move
 * them in the document).
 */
function decoratePullQuotes(doc) {
  for (const bq of doc.querySelectorAll("blockquote")) {
    bq.classList.add("blog-pullquote");
  }
}

/**
 * Improve every <img>:
 *   - loading="lazy" (browsers won't lazy-load if attribute is set to eager)
 *   - decoding="async"
 *   - wrap in <figure> with a <figcaption> when alt text is meaningful
 *   - add a CSS class for rounded corners + spacing
 */
function enhanceImages(doc) {
  for (const img of doc.querySelectorAll("img")) {
    if (!img.getAttribute("loading")) img.setAttribute("loading", "lazy");
    if (!img.getAttribute("decoding")) img.setAttribute("decoding", "async");
    img.classList.add("blog-img");
    const alt = (img.getAttribute("alt") || "").trim();
    // Wrap into figure if not already.
    if (img.parentElement && img.parentElement.tagName !== "FIGURE") {
      const fig = doc.createElement("figure");
      fig.className = "blog-figure";
      img.replaceWith(fig);
      fig.appendChild(img);
      if (alt && alt.length > 12 && alt.length < 200) {
        const cap = doc.createElement("figcaption");
        cap.textContent = alt;
        fig.appendChild(cap);
      }
    }
  }
}

/**
 * Build TOC from H2s (we deliberately skip H3 to keep the side rail
 * scannable — NerdWallet / Bankrate style).
 */
function buildToc(doc) {
  return Array.from(doc.querySelectorAll("h2")).map((h) => ({
    id: h.getAttribute("id"),
    text: textOf(h),
    level: 2,
  }));
}

/**
 * Quick Answer = the first 1–2 sentences of the first <p> after any leading
 * intro paragraph. We cap at 320 chars so it stays a card, not a block.
 */
function extractQuickAnswer(doc) {
  const firstP = doc.querySelector("p");
  if (!firstP) return null;
  const text = textOf(firstP);
  if (!text || text.length < 60) return null;
  // Take first 1-2 sentences, max 320 chars.
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
  let out = "";
  for (const s of sentences) {
    if ((out + s).length > 320) break;
    out += s;
  }
  out = out.trim();
  if (!out || out.length < 40) return null;
  return out;
}

export function processArticleHtml(rawHtml, opts = {}) {
  const { title } = opts;
  if (!rawHtml || typeof window === "undefined") {
    return { html: rawHtml || "", toc: [], faqs: [], quickAnswer: null };
  }
  // Wrap so <body> always has the parsed nodes regardless of input shape.
  const doc = new DOMParser().parseFromString(`<!doctype html><html><body>${rawHtml}</body></html>`, "text/html");

  // Order matters: dedupe → promote → faq → callouts → images → ids → toc.
  promotePseudoHeadings(doc);
  dedupeLeadingTitle(doc, title);
  const faqs = extractFaqs(doc);
  applyCallouts(doc);
  decoratePullQuotes(doc);
  enhanceImages(doc);
  addHeadingIds(doc);

  const toc = buildToc(doc);
  const quickAnswer = extractQuickAnswer(doc);

  return {
    html: doc.body.innerHTML,
    toc,
    faqs,
    quickAnswer,
  };
}

// Exported just so unit/regression tests can probe sub-steps if needed.
export const __test__ = { slugify, stripTags };
